/**
 * Alpine Plugins
 *
 * This library provides extra directives and magics.
 */

const placements = ['auto', 'top', 'bottom', 'left', 'right'];
const triggers = ['click', 'hover', 'focus', 'manual'];
let popoverClickOutsideListenerInitialized = false;

// Get dependent modifier value
function getModifierValue(modifiers, index, modifier, defaultValue = '') {
    let nextValue;

    if (modifiers[index] === modifier) {
        nextValue = modifiers[index + 1];
    } else {
        return defaultValue;
    }

    if (typeof nextValue === 'undefined') {
        return defaultValue;
    }

    if (typeof defaultValue === 'number') {
        nextValue = Number(nextValue);
    }

    return nextValue;
}

// Evaluate the given expression using Alpine.evaluate()
function evaluateExpression(element, expression, isRaw) {
    if (isRaw) {
        return expression;
    }

    let evaluated = Alpine.evaluate(element, expression);

    if (typeof evaluated === 'function') {
        evaluated = evaluated();
    }

    return evaluated;
}

// Bootstrap-based tooltip
function tooltip(element, modifiers, expression) {
    let placement = 'top';
    let trigger = [];
    let show = 0;
    let hide = 0;
    let isHtml = false;
    let isRaw = false;

    modifiers.forEach((modifier, index) => {
        if (placements.includes(modifier)) {
            placement = modifier;
            return;
        }

        if (triggers.includes(modifier)) {
            trigger.push(modifier);
        }

        show = getModifierValue(modifiers, index, 'show', show);
        hide = getModifierValue(modifiers, index, 'hide', hide);
        isHtml = modifier === 'html';
        isRaw = modifier === 'raw';
    });

    if (trigger.length === 0) {
        trigger.push('hover');

        if (Helper.isMobile()) {
            trigger.push('focus');
        }
    }

    let config = {
        boundary: 'window',
        container: 'body',
        placement: placement,
        trigger: trigger.join(' '),
        html: isHtml,
        delay: { show, hide },
    };

    let evaluated = evaluateExpression(element, expression, isRaw);

    if (typeof evaluated === 'object') {
        Object.assign(config, evaluated);
    } else {
        Object.assign(config, { title: evaluated });
    }

    if (IS_BOOTSTRAP4) {
        if ($(element).data('bs.tooltip')) {
            Object.assign($(element).data('bs.tooltip').config, config);
        } else {
            $(element).tooltip(config);
        }
    } else if (IS_BOOTSTRAP5) {
        const tooltip = bootstrap.Tooltip.getOrCreateInstance(element, config);
        tooltip.setContent({ '.tooltip-inner': evaluated });
    }
}

// Bootstrap-based popover
function popover(element, modifiers, expression) {
    let placement = 'right';
    let trigger = [];
    let show = 0;
    let hide = 0;
    let isHtml = false;
    let isRaw = false;

    modifiers.forEach((modifier, index) => {
        if (placements.includes(modifier)) {
            placement = modifier;
            return;
        }

        if (triggers.includes(modifier)) {
            trigger.push(modifier);
        }

        show = getModifierValue(modifiers, index, 'show', show);
        hide = getModifierValue(modifiers, index, 'hide', hide);
        isHtml = modifier === 'html';
        isRaw = modifier === 'raw';
    });

    if (trigger.length === 0) {
        trigger.push('click');
    }

    let defaultConfig, allowListName;

    if (IS_BOOTSTRAP4) {
        defaultConfig = $.fn.popover.Constructor.Default;
        allowListName = 'whiteList';
    } else if (IS_BOOTSTRAP5) {
        defaultConfig = bootstrap.Popover.Default;
        allowListName = 'allowList';
    }

    const allowList = Helper.cloneDeep(defaultConfig[allowListName]);
    allowList['*'].push(/x-[a-z\.\:\-\_/]*/);

    let config = {
        boundary: 'window',
        container: 'body',
        placement: placement,
        trigger: trigger.join(' '),
        html: isHtml,
        delay: { show, hide },
        [allowListName]: allowList,
    };

    let evaluated = evaluateExpression(element, expression, isRaw);

    if (typeof evaluated === 'object' && !Array.isArray(evaluated)) {
        const providedAllowList = evaluated.allowList ?? evaluated.whiteList ?? null;

        if (providedAllowList) {
            for (const [key, value] of Object.entries(providedAllowList)) {
                if (Object.hasOwn(allowList, key)) {
                    allowList[key].push(...value);
                } else {
                    allowList[key] = value;
                }
            }

            delete evaluated.allowList;
            delete evaluated.whiteList;
        }

        Object.assign(config, evaluated);
    } else {
        if (typeof evaluated === 'string') {
            evaluated = String(evaluated).split('|', 2);
        }

        let [title, content] = evaluated;

        if (typeof content === 'undefined') {
            content = title;
            title = '';
        }

        Object.assign(config, { title, content });
    }

    if (config.data) {
        const id = `p${Date.now()}${Math.floor(Math.random() * (9999 - 1000) + 1000)}`;

        if (!window._popoverData) {
            window._popoverData = {};
        }

        window._popoverData[id] = () =>
            Helper.mergeObjects(
                Object.defineProperties(
                    {},
                    {
                        close: {
                            enumerable: true,
                            value() {
                                if (IS_BOOTSTRAP4) {
                                    $(element).popover('hide');
                                } else if (IS_BOOTSTRAP5) {
                                    bootstrap.Popover.getInstance(element).hide();
                                }
                            },
                        },
                    }
                ),
                config.data
            );

        config.template = defaultConfig.template.replace('class="popover"', `class="popover" x-data="_popoverData.${id}"`);

        element._x_cleanups.push(() => delete window._popoverData[id]);
    }

    if (IS_BOOTSTRAP4) {
        if ($(element).data('bs.popover')) {
            Object.assign($(element).data('bs.popover').config, config);
        } else {
            $(element).popover(config);
        }
    } else if (IS_BOOTSTRAP5) {
        const popover = bootstrap.Popover.getOrCreateInstance(element, config);

        popover.setContent({
            '.popover-header': config.title,
            '.popover-body': config.content,
        });
    }

    if (!popoverClickOutsideListenerInitialized) {
        document.addEventListener('click', (event) => {
            const target = event.target;

            if (target.closest('.popover') !== null) return;

            const popoverId = target.closest('[data-x-popover-inited]')?.getAttribute('aria-describedby') ?? null;

            document.querySelectorAll('[data-x-popover-inited]').forEach((element) => {
                if (IS_BOOTSTRAP4) {
                    if ($(element).data('bs.popover')?.tip?.id !== popoverId) {
                        $(element).popover('hide');
                    }
                } else if (IS_BOOTSTRAP5) {
                    const popover = bootstrap.Popover.getInstance(element);

                    if (popover?.tip?.id !== popoverId) {
                        popover?.hide();
                    }
                }
            });
        });

        popoverClickOutsideListenerInitialized = true;
    }
}

// List of directives
const directives = {
    tooltip,
    popover,
};

// Observe the elements that have the directives and trigger the functions when they are visible
const intersectionObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        const element = entry.target;
        intersectionObserver.unobserve(element);

        Array.from(element.attributes).forEach((attribute) => {
            if (!attribute.name.startsWith('x-')) return;

            Object.keys(directives).forEach((directive) => {
                if (attribute.name.startsWith(`x-${directive}`) && !element.hasAttribute(`data-x-${directive}-inited`)) {
                    directives[directive](element, attribute.name.split('.').slice(1), attribute.value);
                    element.setAttribute(`data-x-${directive}-inited`, '1');
                }
            });
        });
    });
});

// Export the function for plugins registration
export default function (Alpine) {
    Object.keys(directives).forEach((directive) => {
        Alpine.directive(directive, (element, { modifiers, expression }, { evaluateLater, effect, cleanup }) => {
            if (modifiers.includes('raw')) {
                intersectionObserver.observe(element);
            } else {
                const track = evaluateLater(expression);

                effect(() => track(() => intersectionObserver.observe(element)));
            }

            cleanup(() => {
                if (!element.hasAttribute(`data-x-${directive}-inited`)) return;

                if (IS_BOOTSTRAP4) {
                    $(element)[directive]('dispose');
                } else if (IS_BOOTSTRAP5) {
                    window.setTimeout(() => bootstrap[directive.charAt(0).toUpperCase() + directive.substring(1)].getInstance(element)?.dispose(), 500);
                }
            });
        });
    });

    Alpine.directive('prop', (element, { value, expression }, { evaluate }) => {
        const data = Alpine.closestDataStack(element)[0];
        const name = value.replace(/-(\w)/g, (_, word) => word.toUpperCase());

        if (data) {
            data[name] = evaluate(expression);
        }
    });

    Alpine.directive('removed', (element, { expression }, { evaluate, cleanup }) => {
        cleanup(() => evaluate(expression));
    });

    Alpine.directive('remove-after-init', (element, { expression }, { evaluate }) => {
        element.remove();

        if (expression) evaluate(expression);
    });

    Alpine.magic('component', () => Helper.getAlpineComponent);

    Alpine.magic('parent', (element) => {
        const dataStack = Alpine.closestDataStack(element).slice(1);

        return dataStack.length > 0 ? Alpine.mergeProxies(dataStack) : undefined;
    });

    // Directives and magics for component's reinitialization
    const reinitWatcher = function (keys) {
        const root = this.$root;

        (Array.isArray(keys) ? keys : [keys]).forEach((key) =>
            this.$watch(key, () => {
                Alpine.destroyTree(root);
                Alpine.initTree(root);
            })
        );
    };

    Alpine.directive('reinit-watcher', (element, { expression }, { evaluate }) => reinitWatcher.call(Alpine.$data(element), evaluate(expression)));

    Alpine.magic('reinitWatcher', () => reinitWatcher);

    const reinitChildsWatcher = function (keys) {
        const elements = this.$root.querySelectorAll('[x-data], [x-init]');

        (Array.isArray(keys) ? keys : [keys]).forEach((key) =>
            this.$watch(key, () =>
                elements.forEach((element) => {
                    Alpine.destroyTree(element);
                    Alpine.initTree(element);
                })
            )
        );
    };

    Alpine.directive('reinit-childs-watcher', (element, { expression }, { evaluate }) => reinitChildsWatcher.call(Alpine.$data(element), evaluate(expression)));

    Alpine.magic('reinitChildsWatcher', () => reinitChildsWatcher);
}
